import React from "react";
import styled from "styled-components";
import { Modal, Button, Typography, Space, Card, Tag } from "antd";
import {
  CrownOutlined,
  CheckCircleOutlined,
  ArrowUpOutlined,
  HomeOutlined,
  DollarOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getPricingSchedule, getRecommendedTier } from "../lib/pricing";
import { trackEvent } from "../helpers/mixpanel";

const { Title, Text, Paragraph } = Typography;

const UpgradeModal = ({
  visible,
  onCancel,
  currentUnits,
  currentTier,
  pricingTierGroup = "market_2026",
}) => {
  const navigate = useNavigate();

  const schedule = getPricingSchedule(pricingTierGroup);
  const currentTierInfo = currentTier ? schedule[currentTier] : null;
  const recommendedTier = getRecommendedTier(
    currentUnits,
    pricingTierGroup,
    new Date()
  );

  const handleUpgradeClick = (tierKey) => {
    try {
      trackEvent("upgrade_modal_cta_click", {
        current_tier: currentTier,
        current_units: currentUnits,
        target_tier: tierKey,
        source: "unit_limit",
      });
    } catch (error) {
      console.warn("[UpgradeModal] Failed to track upgrade click:", error);
    }

    // Navigate to pricing page with the selected plan
    navigate(`/#pricing`);
    onCancel();
  };

  const getTierIcon = (tierName) => {
    switch (tierName.toLowerCase()) {
      case "starter":
        return <HomeOutlined />;
      case "pro":
        return <DollarOutlined />;
      case "business":
        return <CrownOutlined />;
      case "growth":
        return <ToolOutlined />;
      case "scale":
        return <CrownOutlined />;
      default:
        return <CrownOutlined />;
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center" }}>
          <CrownOutlined
            style={{
              fontSize: "2rem",
              color: "var(--color-primary)",
              marginBottom: "1rem",
            }}
          />
          <Title level={3} style={{ margin: 0 }}>
            Upgrade Your Plan
          </Title>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
    >
      <Content>
        <CurrentStatus>
          <Text type="secondary">
            You currently have <strong>{currentUnits} units</strong>
            {currentTierInfo && (
              <>
                {" "}
                on the <strong>{currentTierInfo.name}</strong> plan (limit:{" "}
                {currentTierInfo.unitCap} units)
              </>
            )}
          </Text>
        </CurrentStatus>

        <UpgradeOptions>
          <Title level={4}>Choose Your Upgrade Path</Title>

          <PlansGrid>
            {Object.entries(schedule)
              .filter(([, plan]) => plan.unitCap > currentUnits)
              .map(([key, plan]) => (
                <PlanCard
                  key={key}
                  $recommended={plan.name === recommendedTier?.name}
                >
                  {plan.name === recommendedTier?.name && (
                    <RecommendedBadge>
                      <CheckCircleOutlined /> Recommended
                    </RecommendedBadge>
                  )}

                  <PlanHeader>
                    <PlanIcon>{getTierIcon(plan.name)}</PlanIcon>
                    <PlanName>{plan.name}</PlanName>
                    <PlanTagline>{plan.tagline}</PlanTagline>
                  </PlanHeader>

                  <PlanPrice>
                    <PriceAmount>${plan.monthlyUSD}</PriceAmount>
                    <PricePeriod>/month</PricePeriod>
                  </PlanPrice>

                  <UnitLimit>
                    Up to <strong>{plan.unitCap} units</strong>
                  </UnitLimit>

                  <PlanFeatures>
                    {plan.features.slice(0, 4).map((feature, index) => (
                      <FeatureItem key={index}>
                        <CheckCircleOutlined
                          style={{ color: "var(--color-primary)" }}
                        />
                        <Text>{feature}</Text>
                      </FeatureItem>
                    ))}
                    {plan.features.length > 4 && (
                      <FeatureItem>
                        <Text type="secondary">
                          +{plan.features.length - 4} more features
                        </Text>
                      </FeatureItem>
                    )}
                  </PlanFeatures>

                  <UpgradeButton
                    type="primary"
                    size="large"
                    icon={<ArrowUpOutlined />}
                    onClick={() => handleUpgradeClick(key)}
                    $recommended={plan.name === recommendedTier?.name}
                  >
                    Upgrade to {plan.name}
                  </UpgradeButton>
                </PlanCard>
              ))}
          </PlansGrid>
        </UpgradeOptions>

        <BenefitsSection>
          <Title level={4}>Why Upgrade Now?</Title>
          <BenefitsList>
            <BenefitItem>
              <CheckCircleOutlined style={{ color: "var(--color-primary)" }} />
              <Text>Add units to your portfolio</Text>
            </BenefitItem>
            <BenefitItem>
              <CheckCircleOutlined style={{ color: "var(--color-primary)" }} />
              <Text>Advanced AI automation features</Text>
            </BenefitItem>
            <BenefitItem>
              <CheckCircleOutlined style={{ color: "var(--color-primary)" }} />
              <Text>Priority support & faster responses</Text>
            </BenefitItem>
            <BenefitItem>
              <CheckCircleOutlined style={{ color: "var(--color-primary)" }} />
              <Text>Advanced reporting and analytics</Text>
            </BenefitItem>
          </BenefitsList>
        </BenefitsSection>

        <Footer>
          <Text type="secondary">
            Need more than 200 units? <a href="/contact">Contact us</a> for
            enterprise pricing.
          </Text>
        </Footer>
      </Content>
    </Modal>
  );
};

export default UpgradeModal;

// Styled Components
const Content = styled.div`
  padding: 1rem 0;
`;

const CurrentStatus = styled.div`
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const UpgradeOptions = styled.div`
  margin-bottom: 2rem;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
`;

const PlanCard = styled(Card)`
  border-radius: 12px;
  border: 2px solid
    ${(props) => (props.$recommended ? "var(--color-primary)" : "#e2e8f0")};
  box-shadow: ${(props) =>
    props.$recommended
      ? "0 4px 12px rgba(44, 95, 45, 0.15)"
      : "0 2px 8px rgba(0, 0, 0, 0.1)"};
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const RecommendedBadge = styled(Tag)`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-primary);
  color: white;
  border: none;
  font-weight: 600;
  z-index: 1;
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const PlanIcon = styled.div`
  font-size: 2rem;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
`;

const PlanName = styled(Title)`
  margin: 0 0 0.5rem 0 !important;
  color: var(--color-primary);
`;

const PlanTagline = styled(Text)`
  color: #6b7280;
  font-size: 0.9rem;
`;

const PlanPrice = styled.div`
  text-align: center;
  margin-bottom: 1rem;
`;

const PriceAmount = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
`;

const PricePeriod = styled.span`
  color: #6b7280;
  font-size: 1rem;
`;

const UnitLimit = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 0.9rem;
`;

const PlanFeatures = styled.div`
  margin-bottom: 1.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const UpgradeButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-weight: 600;
  background: ${(props) =>
    props.$recommended ? "var(--color-primary)" : "white"};
  border-color: ${(props) =>
    props.$recommended ? "var(--color-primary)" : "var(--color-primary)"};
  color: ${(props) => (props.$recommended ? "white" : "var(--color-primary)")};

  &:hover {
    background: ${(props) =>
      props.$recommended
        ? "var(--color-primary-dark)"
        : "var(--color-primary)"};
    border-color: ${(props) =>
      props.$recommended
        ? "var(--color-primary-dark)"
        : "var(--color-primary)"};
    color: white;
  }
`;

const BenefitsSection = styled.div`
  margin-bottom: 2rem;
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 6px;
`;

const Footer = styled.div`
  text-align: center;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  margin-top: 2rem;
`;
